import type { RouterOutputs } from "@apps/server/trpc/routers";
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconFilter, IconPhone, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColDef, ICellRendererParams, RowClickedEvent, SelectionChangedEvent } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useCallback, useMemo, useRef, useState } from "react";
import { AgGridTable } from "~/components/ag-grid-table";
import { useConfigStore } from "~/dexie/store";
import { useBoolean } from "~/hooks/use-boolean";
import { errorNotification, successNotification } from "~/lib/notifications";
import { trpc } from "~/lib/trpc";
import { DealStatusBadge } from "./$dealId";

type Deal = RouterOutputs["deal"]["getDeals"][number];

const dealFilters = [
  { value: "all", label: "All" },
  { value: "agreements", label: "Signed" },
  { value: "offers", label: "Quotes" },
  { value: "partially_signed", label: "Partially Signed" },
  { value: "leads", label: "Leads" },
] as const satisfies ReadonlyArray<{ label: string; value: string }>;

type DealFilterType = (typeof dealFilters)[number]["value"];

export const Route = createFileRoute("/_authenticated/deals/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc } }) => {
    const deals = await queryClient.ensureQueryData(trpc.deal.getDeals.queryOptions());
    return { deals };
  },
});

function RouteComponent() {
  const [filter, setFilter] = useQueryState(
    "filter",
    parseAsStringLiteral(dealFilters.map((f) => f.value)).withDefault("all"),
  );
  const [quickFilter, setQuickFilter] = useState("");
  const [selectedDealIds, setSelectedDealIds] = useState<number[]>([]);
  const gridRef = useRef<AgGridReact<Deal>>(null);
  const navigate = useNavigate();
  const loaderData = Route.useLoaderData();
  const deleteModal = useBoolean();
  const configs = useConfigStore((state) => state.configs);
  const getConfigByField = useConfigStore((state) => state.getConfigByField);
  const deals = useQuery({
    ...trpc.deal.getDeals.queryOptions(),
    initialData: loaderData.deals,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 10_000,
  });
  const qc = useQueryClient();

  const deleteDeals = useMutation(
    trpc.deal.delete.mutationOptions({
      onSuccess: (_, vars) => {
        gridRef.current?.api.deselectAll();
        setSelectedDealIds([]);
        qc.invalidateQueries(trpc.deal.getDeals.queryOptions());
        successNotification(`Deleted ${vars.ids.length} deals`);
        deleteModal.close();
      },
      onError: (error) => {
        errorNotification(error.message);
      },
    }),
  );

  const data = useMemo(() => {
    return deals.data?.filter((deal) => {
      if (filter === "agreements") return deal.status === "agreement";
      if (filter === "partially_signed") return deal.status === "partially_signed";
      if (filter === "offers") return deal.status === "offer";
      if (filter === "leads") return deal.status === "lead";
      return true;
    });
  }, [filter, deals.data]);

  const defaultColDef = useMemo<ColDef<Deal>>(
    () => ({
      sortable: true,
      resizable: true,
      enableRowGroup: true,
    }),
    [],
  );

  const columns = useMemo<ColDef<Deal>[]>(
    () => [
      {
        colId: "name",
        headerName: getConfigByField("crm.customer.name")?.label || "",
        width: 250,
        valueGetter: ({ data }) => data?.customer?.name ?? null,
        cellDataType: "text",
        filter: "agSetColumnFilter",
      },
      {
        field: "id",
        headerName: "Id",
        width: 250,
        cellDataType: "number",
      },
      {
        colId: "email",
        headerName: getConfigByField("crm.customer.email")?.label || "",
        width: 300,
        valueGetter: ({ data }) => data?.customer?.email ?? null,
        cellDataType: "text",
        filter: "agTextColumnFilter",
      },
      {
        colId: "phone",
        headerName: getConfigByField("crm.customer.phone")?.label || "",
        width: 200,
        valueGetter: ({ data }) => data?.customer?.phone ?? null,
        cellDataType: "text",
        filter: "agTextColumnFilter",
        cellRenderer: ({ value }: ICellRendererParams<Deal, string | null>) => {
          if (!value) return null;
          return (
            <Anchor
              href={`tel:${value}`}
              onClick={(event) => event.stopPropagation()}
              c="gray.0"
              bdrs="xl"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                textDecoration: "none",
              }}>
              <IconPhone size={13} stroke={2.5} />
              {value}
            </Anchor>
          );
        },
      },
      {
        colId: "status",
        headerName: getConfigByField("crm.deal.status")?.label || "",
        width: 200,
        valueGetter: ({ data }) => data?.status ?? null,
        cellDataType: "text",
        filter: "agSetColumnFilter",
        cellRenderer: ({ data }: ICellRendererParams<Deal>) =>
          data ? <DealStatusBadge status={data.status} size="md" /> : null,
      },
      {
        colId: "date",
        headerName: getConfigByField("crm.deal.serviceDate")?.label || "",
        width: 260,
        valueGetter: ({ data }) => {
          if (!data?.serviceDate) return null;
          const serviceDate = new Date(data.serviceDate);
          serviceDate.setHours(0, 0, 0, 0);
          return serviceDate;
        },
        cellDataType: "date",
        filter: "agDateColumnFilter",
        filterParams: { inRangeInclusive: true },
        valueFormatter: ({ value }) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
      },
      {
        colId: "createdAt",
        headerName: "Created At",
        valueGetter: ({ data }) => (data?.createdAt ? new Date(data.createdAt) : null),
        cellDataType: "date",
        filter: "agDateColumnFilter",
        filterParams: { inRangeInclusive: true },
        valueFormatter: ({ value }) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm") : ""),
      },
      {
        colId: "totalAmount",
        headerName: getConfigByField("crm.deal.totalAmount")?.label || "",
        width: 220,
        valueGetter: ({ data }) => (data?.totalAmount ? Number.parseFloat(data.totalAmount) : null),
        valueFormatter: ({ data }) => data?.totalAmount ?? "",
        cellDataType: "number",
      },
      {
        colId: "totalToPay",
        headerName: getConfigByField("crm.deal.totalToPay")?.label || "",
        width: 220,
        valueGetter: ({ data }) => (data?.totalToPay ? Number.parseFloat(data.totalToPay) : null),
        valueFormatter: ({ data }) => data?.totalToPay ?? "",
        cellDataType: "number",
      },
      {
        colId: "leftToPay",
        headerName: getConfigByField("crm.deal.leftToPay")?.label || "",
        width: 220,
        valueGetter: ({ data }) => (data?.leftToPay ? Number.parseFloat(data.leftToPay) : null),
        valueFormatter: ({ data }) => data?.leftToPay ?? "",
        cellDataType: "number",
      },
    ],
    [configs],
  );

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<Deal>) => {
      if (!event.data || isInteractiveTarget(event.event?.target)) return;
      void navigate({ to: "/deals/$dealId", params: { dealId: event.data.id.toString() } });
    },
    [navigate],
  );

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent<Deal>) => {
    setSelectedDealIds(event.api.getSelectedRows().map((deal) => deal.id));
  }, []);

  return (
    <Flex direction="column" h="100dvh" mih={0}>
      <Flex p="md" justify="space-between">
        <Flex gap="xs">
          <TextInput
            placeholder="Search Deals"
            leftSection={<IconSearch size={18} />}
            value={quickFilter}
            onChange={(event) => setQuickFilter(event.currentTarget.value)}
          />
          <Select
            variant="filled"
            leftSection={<IconFilter size={22} />}
            value={filter}
            onChange={(value) => setFilter((value ?? "all") as DealFilterType)}
            data={dealFilters}
          />
        </Flex>
        <Flex gap="xs">
          <Tooltip label="New Deal" position="bottom" withArrow>
            <ActionIcon color="blue" size="lg" variant="light" onClick={() => navigate({ to: "/deals/new" })}>
              <IconPlus />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete Deal" position="bottom" withArrow>
            <ActionIcon
              color="red"
              size="lg"
              variant="light"
              disabled={selectedDealIds.length === 0}
              onClick={deleteModal.open}>
              <IconTrash />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Flex>

      <Box flex={1} mih={0}>
        <AgGridTable<Deal>
          ref={gridRef}
          tableName="deals"
          persistView
          height="100%"
          rowData={data}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilter}
          cacheQuickFilter
          pagination={false}
          rowNumbers
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            selectAll: "filtered",
            enableClickSelection: false,
          }}
          groupDisplayType="multipleColumns"
          getRowId={({ data: deal }) => deal.id.toString()}
          getRowStyle={({ data: deal }) => (deal ? { cursor: "pointer" } : undefined)}
          onRowClicked={handleRowClicked}
          onSelectionChanged={handleSelectionChanged}
        />
      </Box>

      <Modal opened={deleteModal.state} onClose={deleteModal.close} title={<Title order={5}>Delete Deals</Title>}>
        <Text>Are you sure you want to delete {selectedDealIds.length} deals?</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="neutral" onClick={deleteModal.close}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleteDeals.isPending}
            onClick={() => deleteDeals.mutate({ ids: selectedDealIds })}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Flex>
  );
}

function isInteractiveTarget(target: EventTarget | null | undefined) {
  return (
    target instanceof Element && Boolean(target.closest("a, button, input, [role='checkbox'], .ag-selection-checkbox"))
  );
}
